import { useState } from 'react';
import { Redirect } from 'react-router-dom';
import { Box, Button, Dialog, DialogContent, Typography } from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';

import useUserStore from 'src/stores/user';
import { WithoutHeader, DesktopLayout } from 'src/layout';
import useIsMobileSize from 'src/hooks/useIsMobileSize';
import { ROUTES_URL } from 'src/constants/routes';
import useGoTo from 'src/hooks/useGoTo';

const slides = [
    {
        icon: '🌍',
        title: 'Добро пожаловать',
        description: 'Лидсмания помогает находить людей, которые уже ищут товары или услуги вашей тематики.',
    },
    {
        icon: '🎯',
        title: 'Опишите, какие заявки нужны',
        description: 'Расскажите о своей услуге, клиентах и регионе. На этой основе вы настроите поток лидов.',
    },
    {
        icon: '📥',
        title: 'Получайте подходящие лиды',
        description: 'Поток находит новые запросы из доступных источников. Ваши отметки помогают точнее отбирать лиды.',
    },
    {
        icon: '🎁',
        title: '500 баллов за анкету',
        description: 'Заполните анкету о бизнесе и получите 500 баллов для оплаты сервиса.',
    },
];

const useStyles = makeStyles(() =>
    createStyles({
        paper: { maxWidth: 700 },
        content: {
            paddingTop: 24,
            paddingBottom: 24,
            '@media (min-width:600px)': {
                paddingTop: 32,
                paddingBottom: 32,
            },
        },
        slide: {
            animation: '$slideIn 220ms cubic-bezier(0.2, 0, 0, 1) both',
            '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
        },
        '@keyframes slideIn': {
            from: { opacity: 0, transform: 'translate3d(12px, 0, 0)' },
            to: { opacity: 1, transform: 'translate3d(0, 0, 0)' },
        },
    })
);

export default function WelcomeScreen() {
    const isMobile = useIsMobileSize();
    const { isOnboardingCompleted } = useUserStore(({ isOnboardingCompleted }) => ({ isOnboardingCompleted }));
    const classes = useStyles();
    const [step, setStep] = useState(0);
    const goTo = useGoTo();

    if (isOnboardingCompleted) {
        return <Redirect to="/" />;
    }

    const slide = slides[step];
    const content = (
        <DialogContent className={classes.content}>
            <Box display="flex" flexDirection="column" justifyContent="center" minHeight={300} px={isMobile ? 2 : 0}>
                <Box
                    key={step}
                    className={classes.slide}
                    height={260}
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    aria-live="polite"
                >
                    <Box mb={2} component="span" aria-hidden sx={{ fontSize: 56, lineHeight: 1 }}>
                        {slide.icon}
                    </Box>
                    <Box mb={3}>
                        <Typography textAlign="center" variant="h1" color="text.primary">
                            {slide.title}
                        </Typography>
                    </Box>
                    <Typography textAlign="center" variant="body1" color="text.primary">
                        {slide.description}
                    </Typography>
                </Box>
                <Box display="flex" justifyContent="center" alignItems="center" mt={3} mb={2} gap={1.5}>
                    {slides.map((_, index) => (
                        <Box
                            key={index}
                            sx={{
                                width: step === index ? 12 : 10,
                                height: step === index ? 12 : 10,
                                borderRadius: '50%',
                                backgroundColor: step === index ? 'secondary.main' : '#C8D7E6',
                                boxShadow: step === index ? '0 0 0 3px rgba(25,118,210,0.15)' : 'none',
                                transition: 'background-color 180ms ease, box-shadow 180ms ease',
                            }}
                        />
                    ))}
                </Box>
                <Box mt={2} width="100%" maxWidth={isMobile ? 360 : undefined} mx="auto">
                    <Button
                        onClick={() => {
                            if (step === slides.length - 1) {
                                goTo(ROUTES_URL.BEGIN_WORK);
                            } else {
                                setStep((current) => current + 1);
                            }
                        }}
                        variant="contained"
                        size="large"
                        fullWidth
                    >
                        {step < slides.length - 1 ? 'Далее' : 'Начать'}
                    </Button>
                </Box>
            </Box>
        </DialogContent>
    );

    return isMobile ? (
        <WithoutHeader showBack={false}>
            <Dialog open fullScreen>{content}</Dialog>
        </WithoutHeader>
    ) : (
        <DesktopLayout>
            <Dialog open classes={{ paper: classes.paper }}>{content}</Dialog>
        </DesktopLayout>
    );
}
